import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, IconButton, Typography } from '@mui/material';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import { COLORS } from '@src/constants/design-tokens/colors';
import { RouterPath } from '@src/constants/router_path';
import type { RootState } from '@src/store';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import React, { memo, type ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import LinkMenu from './LinkMenu';
import { MessageTaskPanel } from './MessageTaskPanel';
import { FocusModeIndicator } from './navigation/FocusModeIndicator';
import { LeftSection, RightSection, StyledToolbar } from './navigation/NavigationStyles';
import { TypoCheckSwitch } from './navigation/TypoCheckSwitch';
import { UserMenu } from './navigation/UserMenu';

/**
 * ナビゲーションコンポーネントのProps型定義
 */
type NavigationProps = Record<string, never>;

/**
 * ナビゲーションコンポーネント
 *
 * アプリケーションのヘッダーナビゲーションを提供します。
 * レスポンシブデザインに対応し、モバイルではハンバーガーメニューを表示します。
 *
 * @returns {ReactElement} ナビゲーションコンポーネント
 *
 * @example
 * ```tsx
 * <Navigation />
 * ```
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
function Navigation(_props: NavigationProps): ReactElement {
  const { t } = useTranslation();
  // Variables
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => s.auth);
  const authUser = auth.user;
  const isFocusMode = useSelector(selectFocusMode) || false;
  const isAuthEnabled = useFeatureFlag(FeatureFlag.AUTH_ENABLED);

  // States
  const [showMenu, setShowMenu] = useState<boolean>(false);

  /**
   * メニュー開閉トグル
   * @returns {void}
   */
  function handleMenuToggle(): void {
    setShowMenu((prev) => !prev);
  }

  /**
   * ホームへ遷移
   * @returns {void}
   */
  function handleHomeNavigation(): void {
    navigate(RouterPath.index);
  }

  /**
   * メニュー閉じる処理
   * @returns {void}
   */
  const handleMenuClose = useCallback((): void => {
    setShowMenu(false);
  }, []);

  return (
    <React.Fragment>
      <Box
        sx={{
          position: 'relative',
          marginTop: 'calc(-1 * env(safe-area-inset-top))',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <AppBar
          position="static"
          sx={(theme) => ({
            zIndex: 9999,
            width: '100%',
            backgroundColor: isFocusMode
              ? COLORS.focusModeNav.background
              : theme.palette.background.paper,
            color: isFocusMode ? COLORS.focusModeNav.text : theme.palette.text.primary,
            backdropFilter: isFocusMode ? 'blur(15px)' : 'none',
            borderBottom: isFocusMode
              ? `1px solid ${COLORS.focusModeNav.border}`
              : `1px solid ${theme.palette.divider}`,
            boxShadow: isFocusMode
              ? '0 2px 8px rgba(0,0,0,0.35)'
              : theme.palette.mode === 'dark'
                ? '0 8px 24px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.4)'
                : '0 8px 24px rgba(15, 23, 42, 0.16), 0 2px 6px rgba(15, 23, 42, 0.12)',
          })}
          component="header"
          aria-label={t('navigation.mainNavigation')}
        >
          <StyledToolbar>
            {/* 左側セクション: メニューボタン + タイトル */}
            <LeftSection>
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                onClick={handleMenuToggle}
                aria-label={showMenu ? t('navigation.closeMenu') : t('navigation.openMenu')}
                aria-expanded={showMenu}
                aria-controls="main-navigation-menu"
                aria-haspopup="true"
                className="nav-button"
                sx={{
                  marginRight: 0.5,
                  padding: '8px',
                  flexShrink: 0,
                  minWidth: '44px',
                  minHeight: '44px',
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="subtitle1"
                component="span"
                onClick={handleHomeNavigation}
                aria-label={t('navigation.homeLink')}
                className="logo"
                sx={(theme) => ({
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  color: isFocusMode ? COLORS.darkMode.text.primary : theme.palette.text.primary,
                  textShadow: isFocusMode ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
                  '&:hover': {
                    // シンプルな下線のみ。色は黒（フォーカスモードは可読性のため白を維持）
                    color: isFocusMode ? COLORS.darkMode.text.primary : theme.palette.text.primary,
                    textShadow: 'none',
                    textDecoration: 'underline',
                  },
                  '&:focus': {
                    outline: isFocusMode
                      ? '2px solid rgba(255, 255, 255, 0.5)'
                      : `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                })}
              >
                Commmit Resume
              </Typography>
            </LeftSection>

            {/* 右側セクション: 認証 + 言語切り替え */}
            <RightSection>
              <FocusModeIndicator isFocusMode={isFocusMode} />

              <UserMenu user={authUser} isFocusMode={isFocusMode} />

              <TypoCheckSwitch
                isFocusMode={isFocusMode}
                isAuthEnabled={isAuthEnabled}
                isAuthenticated={!!authUser}
              />

              <Box
                sx={{
                  ml: 2,
                  // モバイル時のみ30%小さく表示（タブレット以上はPC表示と同じ）
                  transform: { xs: 'scale(0.7)', sm: 'scale(1)' },
                  transformOrigin: 'center right',
                  // ブレークポイント切り替え時のちらつき防止
                  transition: 'none',
                }}
              >
                <LanguageSwitcher />
              </Box>
            </RightSection>
          </StyledToolbar>
        </AppBar>

        {/* メッセージ・タスク表示エリア */}
        <MessageTaskPanel isScrolling={false} />
      </Box>
      <Box sx={{ height: { xs: '56px', sm: '64px' } }} />
      <LinkMenu show={showMenu} onClose={handleMenuClose} />
    </React.Fragment>
  );
}

export default memo(Navigation);
